import React, { useState, useEffect } from "react";
import {
  Layout,
  Card,
  Button,
  Table,
  Modal,
  Form,
  Input,
  Switch,
  Space,
  Avatar,
  Popconfirm,
  Upload,
  Select,
  Image,
  message,
} from "antd";

import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
  DragOutlined,
} from "@ant-design/icons";

import {
  createTeam,
  deleteTeam,
  getAllTeams,
  updateLeaderStatus,
  updatePublishStatus,
  updateTeam,
  updateTeamImage,
  updateTeamPriority,
} from "../services/team.service";

import ImageGridSorter from "./ImageGridSorter";
import "./teams.css";

const { Content } = Layout;

const Teams = () => {
  const [form] = Form.useForm();

  const [teams, setTeams] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const [imagePreview, setImagePreview] = useState(null);

  const [previewImage, setPreviewImage] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const pageSize = 5;

  const [isOrderView, setIsOrderView] = useState(false);
  const [orderedData, setOrderedData] = useState([]);
  const [loading, setLoading] = useState(false);
  // Fetch

  const fetchTeams = async (page = 1) => {
    try {
      setLoading(true);

      const res = await getAllTeams(page, pageSize);

      setTeams(res.data.data);
      setTotalItems(res.data.pagination.totalItems);
      setCurrentPage(page);
    } catch {
      message.error("Failed to load teams");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeams(currentPage);
  }, [currentPage]);

  //Model

  const showModal = (record = null) => {
    setEditing(record);
    setOpen(true);

    if (record) {
      form.setFieldsValue(record);

      setImagePreview(record.profileImage);
    } else {
      form.resetFields();
      setImagePreview(null);
    }
  };

  // Submit

  const handleSubmit = async (values) => {
    try {
      if (editing) {
        await updateTeam(editing.id, values);

        message.success("Team updated successfully");
      } else {
        if (!values.profileImage) {
          message.error("Please upload profile image");
          return;
        }

        await createTeam(values);

        message.success("Team created successfully");
      }

      fetchTeams(currentPage);

      setOpen(false);
      setEditing(null);
      form.resetFields();
    } catch (error) {
      message.error(error?.response?.data?.message || "Validation Error");
    }
  };

  const handleSaveOrder = async () => {
    try {
      const source = orderedData.length ? orderedData : teams;

      const reorderedItems = source.map((item, index) => ({
        id: item.id,
        priority: index,
      }));

      await updateTeamPriority({
        reorderedItems,
      });

      message.success("Team order updated successfully");

      setOrderedData([]);
    } catch {
      message.error("Failed to update team order");
    }
  };
  // Delete

  const handleDelete = async (id) => {
    await deleteTeam(id);

    message.success("Deleted");

    fetchTeams(currentPage);
  };

  // Publish

  const togglePublish = async (checked, record) => {
    await updatePublishStatus(record.id, { isPublish: checked });

    message.success(
      checked ? "Published Successfully" : "Unpublished Successfully",
    );

    fetchTeams(currentPage);
  };

  // Leader

  const toggleLeader = async (checked, record) => {
    await updateLeaderStatus(record.id, { isLeader: checked });

    message.success(
      checked ? "Leader Assigned Successfully" : "Leader Removed Successfully",
    );

    fetchTeams(currentPage);
  };

  // Table

  const columns = [
    {
      title: "Member",
      render: (_, record) => (
        <Space direction="vertical">
          <Avatar
            src={record.profileImage}
            size={60}
            style={{ cursor: "pointer" }}
            onClick={() => {
              setPreviewImage(record.profileImage);
              setPreviewOpen(true);
            }}
          />

          <Upload
            showUploadList={false}
            beforeUpload={(file) => {
              const reader = new FileReader();

              reader.onload = async (e) => {
                await updateTeamImage(record.id, {
                  profileImage: e.target.result,
                });

                message.success("Image updated");

                fetchTeams(currentPage);
              };

              reader.readAsDataURL(file);

              return false;
            }}
          >
            <Button size="small" icon={<UploadOutlined />}>
              Update
            </Button>
          </Upload>
        </Space>
      ),
    },

    {
      title: "Name",
      render: (_, record) => (
        <div>
          <strong>
            {record.title} {record.firstName} {record.lastName}
          </strong>
          <div style={{ fontSize: 12, color: "#888" }}>
            {record.designation}
          </div>
        </div>
      ),
    },

    {
      title: "Leader",
      render: (_, record) => (
        <Switch
          checked={record.isLeader}
          onChange={(checked) => toggleLeader(checked, record)}
        />
      ),
    },

    {
      title: "Publish",
      render: (_, record) => (
        <Switch
          checked={record.isPublish}
          onChange={(checked) => togglePublish(checked, record)}
        />
      ),
    },

    {
      title: "Actions",
      render: (_, record) => (
        <Space direction="vertical">
          <Button icon={<EditOutlined />} onClick={() => showModal(record)}>
            Edit
          </Button>

          <Popconfirm
            title="Delete this member?"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Layout>
      <Content style={{ padding: 20 }}>
        <Card title="Team Module">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => showModal()}
          >
            Add Team
          </Button>

          <Button
            style={{ marginLeft: 20 }}
            icon={<DragOutlined />}
            onClick={async () => {
              if (!isOrderView) {
                // Fetch ALL teams for reorder view

                const res = await getAllTeams(1, 1000);

                const allTeams = res?.data?.data || [];

                setTeams(allTeams);

                setOrderedData(
                  allTeams.map((i) => ({
                    id: i.id,
                    image: i.profileImage,
                    title: i.firstName,
                    subtitle: i.designation,
                  })),
                );
              } else {
                // Back to paginated list
                fetchTeams(currentPage);
              }

              setIsOrderView(!isOrderView);
            }}
          >
            {isOrderView ? "Back to List" : "Reorder"}
          </Button>
          {isOrderView && (
            <Button
              type="primary"
              htmlType="button"
              style={{ marginTop: 20, marginLeft: 10 }}
              onClick={handleSaveOrder}
            >
              Save Order
            </Button>
          )}

          <br />
          <br />

          {isOrderView && (
            <ImageGridSorter
              data={teams.map((i) => ({
                id: i.id,
                image: i.profileImage,
                title: `${i.firstName} ${i.lastName}`,
                subtitle: i.designation,
              }))}
              onChange={(newOrder) => {
                setOrderedData(newOrder);

                // UI order update
                const updatedTeams = newOrder.map((item) =>
                  teams.find((t) => t.id === item.id),
                );

                setTeams(updatedTeams);
              }}
            />
          )}

          {!isOrderView && (
            <Table
              columns={columns}
              dataSource={teams}
              loading={loading}
              rowKey="id"
              scroll={{ x: 700 }}
              pagination={{
                current: currentPage,
                pageSize,
                total: totalItems,
                onChange: (page) => fetchTeams(page),
              }}
            />
          )}
        </Card>

        {/* Model */}

        <Modal
          title={editing ? "Update Team" : "Add Team"}
          open={open}
          footer={null}
          onCancel={() => setOpen(false)}
        >
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            {!editing && (
              <Form.Item name="profileImage" hidden>
                <Input />
              </Form.Item>
            )}

            <Form.Item name="title" label="Title" rules={[{ required: false }]}>
              <Select>
                <Select.Option value="Mr.">Mr.</Select.Option>
                <Select.Option value="Ms.">Ms.</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="firstName"
              label="First Name"
              rules={[{ required: false }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="lastName"
              label="Last Name"
              rules={[{ required: false }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="designation"
              label="Designation"
              rules={[{ required: false }]}
            >
              <Input />
            </Form.Item>

            {!editing && (
              <Form.Item label="Profile Image">
                <Upload
                  showUploadList={false}
                  beforeUpload={(file) => {
                    const reader = new FileReader();

                    reader.onload = (e) => {
                      form.setFieldsValue({
                        profileImage: e.target.result,
                      });

                      setImagePreview(e.target.result);
                    };

                    reader.readAsDataURL(file);

                    return false;
                  }}
                >
                  <Button icon={<UploadOutlined />}>Upload Image</Button>
                </Upload>

                {imagePreview && (
                  <Image
                    src={imagePreview}
                    width={80}
                    style={{ marginTop: 10 }}
                  />
                )}
              </Form.Item>
            )}

            {!editing && (
              <Form.Item name="isLeader" label="Leader" valuePropName="checked">
                <Switch />
              </Form.Item>
            )}

            {!editing && (
              <Form.Item
                name="isPublish"
                label="Publish"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            )}

            <Form.Item>
              <Button type="primary" htmlType="submit" block>
                {editing ? "Update" : "Save"}
              </Button>
            </Form.Item>
          </Form>
        </Modal>

        {/* image preview */}

        <Modal
          open={previewOpen}
          footer={null}
          onCancel={() => setPreviewOpen(false)}
        >
          <Image src={previewImage} />
        </Modal>
      </Content>
    </Layout>
  );
};

export default Teams;
